import { ButtonCus } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { PasswordInput } from '@/components/common/Input';
import { PASSWORD_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface ChangePasswordCmsFormProps {
    idModal: string
}

export default observer(function ChangePasswordCmsForm({
    idModal
}: ChangePasswordCmsFormProps) {
    // hooks
    const { t } = useTranslation();
    const { id } = useParams();

    // store
    const {
        authStore: { changePasswordCMS },
        accountStore: { getHisoryChangeUser, defaultPagingHistory },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    // form
    // validate schema
    const validateSchema = yup.object().shape({
        passwordNew: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        passwordConfirm: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .oneOf([yup.ref('passwordNew')], 'form_error_validate.password_not_match')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            passwordNew: '',
            passwordConfirm: ''
        }
    });

    // state
    const [showPasswordNew, setShowPasswordNew] = useState<boolean>(false);
    const [showPasswordConfirm, setShowPasswordConfirm] =
        useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        if (!id) return;

        const res = await flowResult(
            changePasswordCMS({
                id: Number(id),
                passwordNew: data?.passwordNew || '',
                passwordConfirm: data?.passwordConfirm || ''
            })
        );

        if (res) {
            form.reset();
            hideModal(idModal);
            showAlertModal({
                id: 'change-password-cms-success',
                type: 'success',
                content: t('messages.change_password_success'),
                onClickXIcon: () => onFetchHistory(Number(id), defaultPagingHistory)
            });
        }
    };

    const onCancel = () => {
        hideModal(idModal);
    };

    const onFetchHistory = async (userId: number, paging: any) => {
        await flowResult(getHisoryChangeUser({ userId }, paging));
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='mt-0'
                autoComplete='off'
            >
                <div className='flex flex-col gap-4'>
                    <FormField
                        control={form.control}
                        name='passwordNew'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.new_password')}</FormLabel>
                                <FormControl>
                                    <PasswordInput
                                        type={!showPasswordNew ? 'password' : 'text'}
                                        placeholder={t('placeholder.enter')}
                                        onToggleIcon={() => setShowPasswordNew(!showPasswordNew)}
                                        autoComplete='off'
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='passwordConfirm'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>
                                    {t('field.confirm_new_password')}
                                </FormLabel>
                                <FormControl>
                                    <PasswordInput
                                        type={!showPasswordConfirm ? 'password' : 'text'}
                                        placeholder={t('placeholder.enter')}
                                        onToggleIcon={() =>
                                            setShowPasswordConfirm(!showPasswordConfirm)}
                                        autoComplete='off'
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className='flex gap-4 mt-6 justify-center'>
                        <ButtonCus variant='outline' size='inModal' type='button' onClick={onCancel}>
                            {t('button.cancel')}
                        </ButtonCus>
                        <ButtonCus variant='default' size='inModal' type='submit'>
                            {t('button.change')}
                        </ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
